/**
 * Colour parsing and WCAG 2.x contrast for the theme.
 *
 * One implementation, used by:
 *   - `withAlpha`, the replacement for hex-suffix string arithmetic
 *     (`cedarPrime + '18'` stops working the moment a token is not a 6-digit hex);
 *   - the palette contrast gate (theme/__tests__/palette.contrast.test.ts);
 *   - the render-contrast audit and the colour-trace parity gate.
 *
 * Nothing here guesses. A string that is not a colour returns `null` from
 * `parseColor` and THROWS from everything that needs a colour, so a typo in a
 * palette value fails a test instead of rendering as black.
 */

export interface Rgba {
  r: number;
  g: number;
  b: number;
  /** 0..1 */
  a: number;
}

const NAMED: Record<string, Rgba> = {
  white: { r: 255, g: 255, b: 255, a: 1 },
  black: { r: 0, g: 0, b: 0, a: 1 },
  transparent: { r: 0, g: 0, b: 0, a: 0 },
};

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const FUNC_RE = /^rgba?\(\s*([^)]+?)\s*\)$/i;

/** Parse `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`, `rgb()`, `rgba()` and the three names RN styles use. */
export function parseColor(input: unknown): Rgba | null {
  if (typeof input !== 'string') return null;
  const s = input.trim();
  const named = NAMED[s.toLowerCase()];
  if (named) return { ...named };

  const hex = HEX_RE.exec(s);
  if (hex) {
    let h = hex[1];
    if (h.length === 3 || h.length === 4) {
      h = h
        .split('')
        .map((ch) => ch + ch)
        .join('');
    }
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
    };
  }

  const fn = FUNC_RE.exec(s);
  if (fn) {
    const parts = fn[1].split(',').map((p) => p.trim());
    if (parts.length !== 3 && parts.length !== 4) return null;
    const nums = parts.map((p) => Number(p));
    if (nums.some((n) => !Number.isFinite(n))) return null;
    const [r, g, b] = nums;
    const a = parts.length === 4 ? nums[3] : 1;
    if ([r, g, b].some((n) => n < 0 || n > 255) || a < 0 || a > 1) return null;
    return { r, g, b, a };
  }
  return null;
}

/** True when the string is a colour this module can read. */
export function isColor(input: unknown): boolean {
  return parseColor(input) !== null;
}

function mustParse(input: unknown, what: string): Rgba {
  const c = parseColor(input);
  if (!c) {
    throw new Error(`theme/contrast: ${what} is not a colour: ${JSON.stringify(input)}`);
  }
  return c;
}

/** `fg` drawn over an OPAQUE `bg` (straight alpha, sRGB space — what RN and CSS both do). */
export function composite(fg: Rgba, bg: Rgba): Rgba {
  const a = fg.a;
  return {
    r: Math.round(fg.r * a + bg.r * (1 - a)),
    g: Math.round(fg.g * a + bg.g * (1 - a)),
    b: Math.round(fg.b * a + bg.b * (1 - a)),
    a: 1,
  };
}

/**
 * Flatten a stack of colours, bottom first, onto an opaque result.
 * `flatten(['#0B1614', 'rgba(255,255,255,0.08)'])` is a frost card on the dark canvas.
 * The bottom layer must be opaque: a translucent ground has no colour of its own.
 */
export function flatten(layersBottomFirst: readonly string[]): Rgba {
  if (layersBottomFirst.length === 0) {
    throw new Error('theme/contrast: flatten needs at least one layer');
  }
  let acc = mustParse(layersBottomFirst[0], 'the bottom layer');
  if (acc.a !== 1) {
    throw new Error(
      `theme/contrast: the bottom layer must be opaque, got ${JSON.stringify(layersBottomFirst[0])}`,
    );
  }
  for (let i = 1; i < layersBottomFirst.length; i++) {
    acc = composite(mustParse(layersBottomFirst[i], `layer ${i}`), acc);
  }
  return acc;
}

/** WCAG 2.x relative luminance of an opaque colour. */
export function relativeLuminance(c: Rgba): number {
  const channel = (v: number): number => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
}

/** WCAG 2.x contrast ratio of two OPAQUE colours (1..21). */
export function contrastRatio(a: Rgba, b: Rgba): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Contrast of an ink on a ground, where either may be translucent.
 * `groundLayers` is bottom-first and its bottom layer must be opaque; the ink is
 * composited over the flattened ground before measuring.
 */
export function inkContrast(ink: string, groundLayersBottomFirst: readonly string[]): number {
  const ground = flatten(groundLayersBottomFirst);
  return contrastRatio(composite(mustParse(ink, 'the ink'), ground), ground);
}

/** `rgba(r,g,b,a)` with alpha rounded to 3 places — one canonical spelling. */
export function toRgbaString(c: Rgba): string {
  const a = Math.round(c.a * 1000) / 1000;
  return `rgba(${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)},${a})`;
}

/** `#RRGGBB` for an opaque colour. Throws on a translucent one: it has no hex without a ground. */
export function toHex(c: Rgba): string {
  if (c.a !== 1) {
    throw new Error('theme/contrast: toHex needs an opaque colour; composite it over its ground first');
  }
  const h = (v: number): string => Math.round(v).toString(16).padStart(2, '0');
  return `#${h(c.r)}${h(c.g)}${h(c.b)}`.toUpperCase();
}

/**
 * The colour at `alpha` (0..1), multiplied into any alpha it already has.
 *
 * This replaces `token + '18'`: that spelling only works while the token is a
 * 6-digit hex, and a themed role can be an `rgba()` string in dark.
 */
export function withAlpha(color: string, alpha: number): string {
  if (!(alpha >= 0 && alpha <= 1)) {
    throw new Error(`theme/contrast: withAlpha needs an alpha in 0..1, got ${alpha}`);
  }
  const c = mustParse(color, 'withAlpha colour');
  return toRgbaString({ ...c, a: c.a * alpha });
}

/** Canonical key for comparing two colour strings by value (`#FFF` equals `rgba(255,255,255,1)`). */
export function colorKey(input: string): string {
  return toRgbaString(mustParse(input, 'colour'));
}
